import { cn } from "@/lib/cn";
import { formatDuration } from "../service-meta";
import { PrecoNaLinha } from "./PrecoNaLinha";
import type { ServicoDaLista } from "./tipos";

type Props = {
  servicos: ServicoDaLista[];
  selecionando: boolean;
  selecionados: Set<string>;
  onSelecionar: (ids: string[], marcar: boolean) => void;
  onAbrir: (id: string) => void;
  podeEditarPreco: boolean;
  /** Especialidades ativas da clínica: só elas viram cartão. Vazio = mostra todas. */
  ativas: Set<string>;
};

const SEM = "Outros procedimentos";
const caixa = "h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border";
const Inativo = () => (
  <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-zinc-500 dark:bg-muted">Inativo</span>
);

/**
 * Procedimentos em cartões pelas especialidades da clínica. Quem serve a mais de uma aparece em cada
 * cartão; quem não serve a nenhuma delas fica em "Outros procedimentos", no fim. Status só como
 * exceção (inativo).
 */
export function TabelaDeServicos({ servicos, selecionando, selecionados, onSelecionar, onAbrir, podeEditarPreco, ativas }: Props) {
  const grupos = new Map<string, ServicoDaLista[]>();
  for (const s of servicos) {
    const daClinica = s.especialidades.filter((e) => !ativas.size || ativas.has(e.id));
    const nomes = daClinica.length ? daClinica.map((e) => e.label) : [SEM];
    for (const n of nomes) grupos.set(n, [...(grupos.get(n) ?? []), s]);
  }
  const ordem = [...grupos.keys()].sort((a, b) => (a === SEM ? 1 : b === SEM ? -1 : a.localeCompare(b, "pt-BR")));
  const clique = (s: ServicoDaLista) => (selecionando ? onSelecionar([s.id], !selecionados.has(s.id)) : onAbrir(s.id));

  return (
    <div className="grid gap-4">
      {ordem.map((g) => {
        const doGrupo = grupos.get(g)!;
        const todos = doGrupo.every((s) => selecionados.has(s.id));
        return (
          <section key={g} className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
            <header className="flex items-center gap-3 border-b border-zinc-100 bg-zinc-50/60 px-4 py-2.5 dark:border-border dark:bg-muted/30">
              {selecionando && (
                <input type="checkbox" checked={todos} onChange={() => onSelecionar(doGrupo.map((s) => s.id), !todos)} aria-label={`Selecionar ${g}`} className={caixa} />
              )}
              <h3 className={cn("text-sm font-semibold", g === SEM ? "text-zinc-500 dark:text-muted-foreground" : "text-zinc-900 dark:text-foreground")}>{g}</h3>
              <span className="text-xs text-zinc-500 dark:text-muted-foreground">
                {doGrupo.length} {doGrupo.length === 1 ? "procedimento" : "procedimentos"}
              </span>
            </header>

            <ul className="divide-y divide-zinc-100 sm:hidden dark:divide-border/50">
              {doGrupo.map((s) => (
                <li key={s.id} onClick={() => clique(s)} className={cn("flex items-center gap-2 px-3 py-3", selecionados.has(s.id) && "bg-brand-50/60 dark:bg-primary/5", s.status === "INACTIVE" && "opacity-60")}>
                  {selecionando && <input type="checkbox" readOnly checked={selecionados.has(s.id)} aria-label={`Selecionar ${s.name}`} className={caixa} />}
                  <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: s.color || "#A1A1AA" }} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-zinc-900 dark:text-foreground">{s.name}</p>
                    <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                      {[formatDuration(s.durationMinutes), s.cobradoPorDente && "Por dente"].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  {s.status === "INACTIVE" && <Inativo />}
                  <PrecoNaLinha id={s.id} priceCents={s.priceCents} podeEditar={podeEditarPreco && !selecionando} />
                </li>
              ))}
            </ul>

            <table className="hidden w-full table-fixed text-sm sm:table">
              <colgroup>
                {selecionando && <col className="w-10" />}
                <col />
                <col className="hidden w-28 md:table-column" />
                <col className="hidden w-28 lg:table-column" />
                <col className="hidden w-24 md:table-column" />
                <col className="w-40" />
              </colgroup>
              <thead className="text-[10.5px] uppercase tracking-wider text-zinc-400 dark:text-muted-foreground">
                <tr>
                  {selecionando && <th />}
                  <th className="px-4 pb-1 pt-2 text-left font-semibold">Nome</th>
                  <th className="hidden px-2 pb-1 pt-2 text-left font-semibold md:table-cell">Cobrança</th>
                  <th className="hidden px-2 pb-1 pt-2 text-left font-semibold lg:table-cell">TUSS</th>
                  <th className="hidden px-2 pb-1 pt-2 text-left font-semibold md:table-cell">Duração</th>
                  <th className="px-4 pb-1 pt-2 text-right font-semibold">Preço</th>
                </tr>
              </thead>
              <tbody>
                {doGrupo.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => clique(s)}
                    className={cn(
                      "cursor-pointer border-t border-zinc-100 transition-colors hover:bg-zinc-50 dark:border-border/50 dark:hover:bg-muted/50",
                      selecionados.has(s.id) && "bg-brand-50/60 dark:bg-primary/5",
                      s.status === "INACTIVE" && "opacity-60",
                    )}
                  >
                    {selecionando && (
                      <td className="pl-4" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selecionados.has(s.id)} onChange={() => onSelecionar([s.id], !selecionados.has(s.id))} aria-label={`Selecionar ${s.name}`} className={caixa} />
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: s.color || "#A1A1AA" }} aria-hidden="true" />
                        <span className="truncate font-semibold text-zinc-900 dark:text-foreground">{s.name}</span>
                        {s.status === "INACTIVE" && <Inativo />}
                      </div>
                    </td>
                    <td className="hidden px-2 py-2.5 text-zinc-600 dark:text-foreground/70 md:table-cell">{s.cobradoPorDente ? "Por dente" : "—"}</td>
                    <td className="hidden px-2 py-2.5 font-mono text-[12px] text-zinc-500 dark:text-muted-foreground lg:table-cell">{s.codigoTuss ?? "—"}</td>
                    <td className="hidden px-2 py-2.5 text-zinc-600 dark:text-foreground/70 md:table-cell">{formatDuration(s.durationMinutes) || "—"}</td>
                    <td className="px-4 py-1.5 text-right">
                      <PrecoNaLinha id={s.id} priceCents={s.priceCents} podeEditar={podeEditarPreco && !selecionando} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
}
